import { useCurrentUser } from "@/hooks/useCurrentUser";

export const TRIAL_DEFAULT_SENDER = "noreply@steddy.ai";

/**
 * Copy for each locked control. Kept in one place so the Settings, Profile and
 * team pages explain the same restriction the same way.
 */
export const TRIAL_LOCK_MESSAGES = {
  inbox:
    "Inbox ingestion is turned off for trial accounts. Upload documents manually from Applications → New Application.",
  commission: "Trial accounts use the default 10% commission. Upgrade to set your own default.",
  automatedEmails:
    "Automated broker emails are off for trial accounts and can't be customized. Upgrade to enable them.",
  contractSender: `Trial accounts always send contracts from ${TRIAL_DEFAULT_SENDER}. Connecting your own mailbox is available on a full account.`,
  signedCopyRecipients:
    "Signed-copy recipients can't be changed on a trial account. Signed contracts are emailed to you only.",
  documentsRecipients:
    "Trial accounts send signed documents to the default recipients. Upgrade to choose who gets them.",
  contractDocuments:
    "Trial accounts use the standard contract. Registering, changing or removing contract documents is available on a full account.",
  aiEngine:
    "The AI underwriting engine is fixed to the default on trial accounts. Upgrade to change it.",
  disclosureWarning:
    "The pre-engagement disclosure warning stays on for trial accounts. Upgrade to change it.",
  autoSendContracts:
    "Trial accounts send the merchant onboarding flow when an approval is confirmed. Upgrade to turn that off.",
  batchAnalysis: "Manual batch analysis runs are not available on trial accounts.",
  orgName: "Your organization name can't be changed on a trial account.",
  team: "Trial accounts are single-user. Upgrade to a full account to invite your team.",
  underwritingRules:
    "Underwriting rules stay at platform defaults on trial accounts. Upgrade to customize them.",
  generic: "This feature is disabled for trial accounts.",
} as const;

export type TrialLockKey = keyof typeof TRIAL_LOCK_MESSAGES;

export function useTrialAccount() {
  const { user } = useCurrentUser();
  const isTrial = user?.is_trial_account === true;
  const recipientEmail = (user?.trial_recipient_email || user?.email || "").trim();

  const lockMessage = (key: TrialLockKey = "generic"): string => TRIAL_LOCK_MESSAGES[key];

  /** Hover copy for the "Sending to" box / merchant email field on an application. */
  const redirectMessage = (capturedEmail?: string | null): string => {
    const captured = (capturedEmail || "").trim();
    const capturedPart = captured
      ? `The captured merchant email is ${captured}.`
      : "No merchant email was captured for this application.";
    return `${capturedPart} During the trial, contracts, signed copies and Plaid links are sent to your email (${recipientEmail}) instead of the merchant so you can test the full flow.`;
  };

  return { isTrial, recipientEmail, lockMessage, redirectMessage };
}
